import AssessmentIcon from '@material-ui/icons/Assessment';
import GetAppIcon from '@material-ui/icons/GetApp';
import MemoryIcon from '@material-ui/icons/Memory';
import PageviewIcon from '@material-ui/icons/Pageview';
import PublishIcon from '@material-ui/icons/Publish';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import OntologyManagerIcon from '../../core/icons/OntologyManager';
import PageLayout from '../../core/PageLayout';
import PrivateRoute from '../../core/PrivateRoute';
import { BASE_OPTIONS } from '../../core/project/constants';
import ProjectContainer from '../../core/project/ProjectContainer';
import ProjectData from '../../core/project/ProjectData';
import ProjectExport from '../../core/project/ProjectExport';
import ProjectPermissions from '../../core/project/ProjectPermissions';
import ProjectSummary from '../../core/project/ProjectSummary';
import { UserConsumer } from '../../core/providers/UserProvider';
import ReportManager from '../../core/report/ReportManager';
import Unsaved from '../../core/Unsaved';
import { ENTITLEMENTS as APPENTITLEMENTS } from '../../core/utils/constants';
import VersionManager from '../../core/version/VersionManager';
import { NS_GS } from '../../grafsync/constants';
import GS from '../../grafsync/grafsync';
import AdvancedSearch from '../advanced-search/AdvancedSearch';
import OntologyImportContainer from '../OntologyImportContainer';
import OntologyManager from '../OntologyManager';
import OntologyProjectDetail from './OntologyProjectDetail';
import OntologyProjectInfoContainer from './OntologyProjectInfoContainer';

const options = [
  {
    name: "Ontology Manager Home",
    url: '',
    icon: <OntologyManagerIcon />
  },
  ...BASE_OPTIONS,
  ...[
    {
      name: "Import",
      url: 'project/import',
      entitlement: APPENTITLEMENTS.CAN_IMPORT,
      icon: <PublishIcon />,
      maxComponent: 1
    },
    {
      name: "Export Project",
      url: 'project/export',
      entitlement: APPENTITLEMENTS.CAN_EXPORT_PROJECT,
      icon: <GetAppIcon />
    },
    {
      name: "Advanced Search",
      url: 'project/search',
      icon: <PageviewIcon />
    },
    {
      name: "Reports",
      url: 'project/report',
      icon: <AssessmentIcon />
    },
    {
      name: "Versions",
      url: 'project/version',
      icon: <MemoryIcon />
    }
  ]];

class OntologyProject extends Component {

  render() {

    const { id, applicationURL, applicationTitle, graph } = this.props;
    const baseLink = APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/detail';
    const refreshKey = id;

    const pageCrumb = <Switch>
      <Route path={applicationURL + "/project/detail"} render={() => <span>Project Home</span>} />
      <Route path={applicationURL + "/project/import"} render={() => <span>Import</span>} />
      <Route path={applicationURL + "/project/export"} render={() => <span>Export Project</span>} />
      <Route path={applicationURL + "/project/edit"} render={() => <span>Project Information</span>} />
      <Route path={applicationURL + "/project/permissions"} render={() => <span>Permissions</span>} />
      <Route path={applicationURL + "/project/search"} render={() => <span>Advanced Search</span>} />
      <Route path={applicationURL + "/project/report"} render={() => <span>Reports</span>} />
      <Route path={applicationURL + "/project/version"} render={() => <span>Versions</span>} />
      <Route render={() => <span>Edit</span>} />
    </Switch>;

    return (
      <UserConsumer>
        {({ user, entitlements }) => {
          // Must have this entitlement to do any of this
          if (!entitlements.includes(ENTITLEMENTS.VIEW_ONTOLOGY_PROJECT)) {
            return <Redirect to={{ pathname: '/' }} />;
          }

          return <PageLayout
            title={applicationTitle}
            baseURL={applicationURL}
            graph={graph}
            id={id}
            options={options}
            optionParams={"#project=" + id}
            layoutTitle={ProjectSummary}
            layoutContainer={ProjectContainer}
            breadcrumb={{
              baseURL: applicationURL,
              title: applicationTitle,
              static: pageCrumb,
              path: ["project", "ontology", "report", "version"],
              url: {
                project: "detail",
                ontology: "ontology",
                report: "report",
                version: "version"
              }
            }}>
            <Switch>
              <Route path={applicationURL + "/project/detail"} render={(props) =>
                <OntologyProjectInfoContainer {...props} {...this.props}
                  passthroughComponent={OntologyProjectDetail}
                  id={refreshKey}
                  user={user}
                  projectType="OntologyProject"
                  graph={graph}
                  project={id} />} />
              <PrivateRoute path={applicationURL + "/project/import"}
                entitlement={
                  [
                    APPENTITLEMENTS.CAN_IMPORT
                  ]
                }
                component={GS.outstandingEdits() > 0 ? Unsaved : OntologyImportContainer}
                baseLink={baseLink}
                projectType="OntologyProject"
                graph={graph}
                id={id}
                project={id} />
              <PrivateRoute path={applicationURL + "/project/export"}
                entitlement={
                  [
                    APPENTITLEMENTS.CAN_EXPORT_PROJECT
                  ]
                }
                component={ProjectExport}
                projectType="OntologyProject"
                exportName="ontologyprojectexport"
                graph={graph}
                id={id} />
              <Route path={applicationURL + "/project/edit"} render={(props) =>
                <ProjectData {...props} {...this.props}
                  id={id}
                  projectType="OntologyProject"
                  entitlement={ENTITLEMENTS.EDIT_ONTOLOGY_PROJECT}
                  graph={graph}
                  project={id} />} />
              <PrivateRoute path={applicationURL + "/project/permissions"}
                entitlement={
                  [
                    APPENTITLEMENTS.EDIT_PERMISSIONS
                  ]
                }
                component={OntologyProjectInfoContainer}
                passthroughComponent={ProjectPermissions}
                id={id}
                projectType="OntologyProject"
                user={user}
                graph={graph}
                project={id} />
              <Route path={applicationURL + "/project/ontology"} render={(props) =>
                <ProjectContainer {...props} {...this.props} component={OntologyManager}
                  id={id}
                  projectType="OntologyProject"
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/search"} render={(props) =>
                <OntologyProjectInfoContainer {...props} {...this.props} passthroughComponent={AdvancedSearch}
                  key={refreshKey}
                  id={id}
                  projectType="OntologyProject"
                  user={user}
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/report"} render={(props) =>
                <OntologyProjectInfoContainer {...props} {...this.props} passthroughComponent={ReportManager}
                  key={refreshKey}
                  id={id}
                  user={user}
                  projectType="OntologyProject"
                  graph={graph}
                  project={id} />} />
              <Route path={applicationURL + "/project/version"} render={(props) =>
                <OntologyProjectInfoContainer {...props} {...this.props} passthroughComponent={VersionManager}
                  key={refreshKey}
                  id={id}
                  user={user}
                  projectType="OntologyProject"
                  exportName="ontologyprojectexport"
                  historyOption={{
                    label: "All Resources Defined By Ontology",
                    resource: true,
                    path: [
                      "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
                      NS_GS + "visibility"
                    ]
                  }}
                  graph={graph}
                  project={id} />} />
            </Switch>
          </PageLayout>;
        }}
      </UserConsumer>
    );
  }
}

OntologyProject.propTypes = {
  applicationTitle: PropTypes.string.isRequired,
  applicationURL: PropTypes.string.isRequired,
  lockedGraphs: PropTypes.array,
  id: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired
};

export default OntologyProject;